import { useState } from "react";
import type { Session } from "../../types";

const MIN_WORDS = 80;
const MIN_DURATION_S = 120;
const PAGE_SIZE = 15;

interface Props {
  sessions: Session[];
  onSelect: (sessionId: number) => void;
}

function formatDuration(s: number | null): string {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function errPer100w(errors: number | undefined, words: number | undefined): string {
  if (errors == null || !words || words < MIN_WORDS) return "—";
  return (errors / words * 100).toFixed(1);
}

function errPerMin(errors: number | undefined, duration_s: number | null): string {
  if (errors == null || !duration_s || duration_s < MIN_DURATION_S) return "—";
  return (errors / duration_s * 60).toFixed(1);
}

const TH = "text-left py-3 px-3 text-[0.65rem] uppercase tracking-[0.14em] text-text-tertiary font-semibold border-b border-border";
const TD = "py-3 px-3 border-b border-border-soft";
const NUM = `${TD} font-mono tabular-nums text-text-secondary`;

const TAB = "px-4 py-2 text-[0.6875rem] uppercase tracking-[0.14em] font-semibold border-b-2 -mb-px transition-colors duration-150";

export default function SessionsTable({ sessions, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState<"general" | "focus">("general");

  if (sessions.length === 0) {
    return <p className="text-text-tertiary italic text-sm py-8 text-center">No sessions yet — start one on the Practice page.</p>;
  }

  // Per-user session number (oldest = 1), independent of the global DB id.
  const ordinalById = new Map<number, number>();
  [...sessions]
    .sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime())
    .forEach((s, i) => ordinalById.set(s.id, i + 1));

  const isFocus = (s: Session) => s.mode === "focus";
  const focusCount = sessions.filter(isFocus).length;
  const tabSessions = sessions.filter((s) => isFocus(s) === (tab === "focus"));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? tabSessions.filter((s) => s.topic?.toLowerCase().includes(q) || s.focus_label?.toLowerCase().includes(q))
    : tabSessions;
  const showFocus = tab === "focus";

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const page_ = Math.min(page, Math.max(0, totalPages - 1));
  const visible = filtered.slice(page_ * PAGE_SIZE, page_ * PAGE_SIZE + PAGE_SIZE);

  const handleQuery = (v: string) => {
    setQuery(v);
    setPage(0);
  };

  const selectTab = (t: "general" | "focus") => {
    setTab(t);
    setPage(0);
  };

  return (
    <div>
      <div className="flex gap-0 mb-4 border-b border-border">
        {(["general", "focus"] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`${TAB} ${tab === t ? "border-warning text-warning" : "border-transparent text-text-tertiary hover:text-ink"}`}
            onClick={() => selectTab(t)}
          >
            {t === "general" ? `General (${sessions.length - focusCount})` : `Focus (${focusCount})`}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={query}
          onChange={(e) => handleQuery(e.target.value)}
          placeholder="Search topics…"
          className="w-full sm:w-72 bg-surface2 border border-border rounded-md px-3 py-1.5 text-sm text-ink placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-border"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className={TH}>#</th>
              <th className={TH}>Date</th>
              {showFocus && <th className={TH}>Focus</th>}
              <th className={TH}>Topic</th>
              <th className={TH}>Duration</th>
              <th className={TH}>Words</th>
              <th className={TH}>Errors</th>
              <th className={TH} title="Errors per 100 words spoken">Err/100w</th>
              <th className={TH} title="Errors per session minute (wall-clock)">Err/min</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={showFocus ? 9 : 8} className="py-8 text-center text-text-tertiary italic text-sm">
                  {q ? "No sessions match your search." : showFocus ? "No focus sessions yet — use “Focus on my mistakes” on the Practice page." : "No general sessions yet."}
                </td>
              </tr>
            ) : visible.map((s) => {
              const words = s.metrics?.student_word_count;
              const errors = s.error_count;
              return (
                <tr
                  key={s.id}
                  onClick={() => onSelect(s.id)}
                  className="cursor-pointer transition-colors duration-150 hover:bg-surface2/50"
                >
                  <td className={NUM}>{ordinalById.get(s.id) ?? "—"}</td>
                  <td className={`${TD} whitespace-normal sm:whitespace-nowrap max-w-[4.5rem] sm:max-w-none`}>
                    {new Date(s.started_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </td>
                  {showFocus && (
                    <td className={TD}>
                      {s.focus_label
                        ? <span className="text-[0.7rem] font-semibold text-accent-deep bg-accent-soft rounded-full px-2 py-0.5 whitespace-nowrap">{s.focus_label}</span>
                        : <span className="text-text-tertiary">—</span>}
                    </td>
                  )}
                  <td className={`${TD} text-ink font-medium`}>{s.topic ?? <span className="text-text-tertiary font-normal">—</span>}</td>
                  <td className={NUM}>{formatDuration(s.duration_s)}</td>
                  <td className={NUM}>{words ?? "—"}</td>
                  <td className={NUM}>{errors ?? "—"}</td>
                  <td className={NUM}>{errPer100w(errors, words)}</td>
                  <td className={NUM}>{errPerMin(errors, s.duration_s)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-text-secondary">
          <span className="text-text-tertiary text-xs">
            {filtered.length} session{filtered.length !== 1 ? "s" : ""}
            {query.trim() ? " found" : ""}
            {" · "}page {page_ + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={page_ === 0}
              className="px-2.5 py-1.5 rounded border border-border text-text-secondary text-sm leading-none disabled:opacity-25 hover:bg-surface2 hover:border-border hover:text-ink transition-colors"
            >
              «
            </button>
            <button
              onClick={() => setPage(page_ - 1)}
              disabled={page_ === 0}
              className="px-2.5 py-1.5 rounded border border-border text-text-secondary text-sm leading-none disabled:opacity-25 hover:bg-surface2 hover:border-border hover:text-ink transition-colors"
            >
              ‹
            </button>
            <button
              onClick={() => setPage(page_ + 1)}
              disabled={page_ >= totalPages - 1}
              className="px-2.5 py-1.5 rounded border border-border text-text-secondary text-sm leading-none disabled:opacity-25 hover:bg-surface2 hover:border-border hover:text-ink transition-colors"
            >
              ›
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page_ >= totalPages - 1}
              className="px-2.5 py-1.5 rounded border border-border text-text-secondary text-sm leading-none disabled:opacity-25 hover:bg-surface2 hover:border-border hover:text-ink transition-colors"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
